import { computed } from 'vue';

import i18n from '@/config/i18n';
import { info } from '@/store/mainInfo';
import { getFromCache } from '@/utils/helpers';

const formatters = {};

/**
 * @param {string} locale
 * @param {string} currency
 * @return {Intl.NumberFormat}
 */
const getFormatter = (locale, currency) => getFromCache(
  () => new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }),
  `${locale}_${currency}`,
  formatters,
);

const currencySymbolsToFix = {
  XOF: 'FCFA',
};

const currencySymbolsCache = {};

/**
 * @param {string} currency
 * @param {Intl.NumberFormat} formatter
 * @return {string}
 */
const getCurrencySymbolFromFormatter = (currency, formatter) => formatter.formatToParts(0)
  .find(({ type }) => type === 'currency')
  ?.value;

/**
 * @param {number} value
 * @param {string} locale
 * @param {string} currency
 * @return {string}
 */
export const formatCurrency = (value, locale, currency) => {
  const currencySymbolToFix = currencySymbolsToFix[currency];
  const formatter = getFormatter(locale, currency);
  const formattedValue = formatter.format(value);

  if (!currencySymbolToFix) {
    return formattedValue;
  }

  const currencySymbol = getFromCache(
    () => getCurrencySymbolFromFormatter(currency, formatter),
    'currency',
    currencySymbolsCache,
  );

  return formattedValue.replace(currencySymbol, currencySymbolToFix);
};

/**
 * @param {string} locale
 * @param {string} currency
 * @return {string}
 */
export const getCurrencySymbol = (locale, currency) => currencySymbolsToFix[currency]
  ?? getCurrencySymbolFromFormatter(currency, getFormatter(locale, currency));

export const currentCurrencySymbol = computed(
  () => getCurrencySymbol(i18n.global.locale.value, info.value.currency),
);

export const formatCurrencyByDefault = (value) => formatCurrency(
  value,
  i18n.global.locale.value,
  info.value.currency,
);
